import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BadgeCheck, Award, ShieldCheck, Sparkles, ArrowRight,
  BarChart3, GraduationCap,
  CheckCircle2, ChevronRight, Star, X, Clock, Target, Briefcase, BookOpen as BookOpenIcon
} from "lucide-react";
import MarketingHeader from "../components/marketing/MarketingHeader";
import Footer from "../components/marketing/Footer";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PROGRAMS } from "./ProgramsPage";
import { PROGRAM_INFO } from "../data/programInfo";

const stats = [
  { value: "10,000+", label: "Students Registered" },
  { value: "30+", label: "Partner Colleges" },
  { value: "7,000+", label: "Internships / Projects" },
  { value: "7,000+", label: "Certificates Issued" },
];

const features = [
  { icon: BadgeCheck, title: "UGC & NEP-2020 Aligned", desc: "Programs designed in line with India's latest education policy and university standards.", image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80" },
  { icon: Award, title: "Verifiable Certificates", desc: "Every certificate has a unique ID that can be verified publicly.", image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80" },
  { icon: ShieldCheck, title: "Secure Payments", desc: "Industry-standard payment processing with end-to-end encryption via Razorpay.", image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80" },
  { icon: Sparkles, title: "120-Hour Structured Training", desc: "Industry-ready curriculum covering theory, practice, and real-world projects.", image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80" },
  { icon: GraduationCap, title: "Mentor Support", desc: "Get guidance from industry experts throughout your internship journey.", image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80" },
  { icon: BarChart3, title: "Live Progress Tracking", desc: "Real-time dashboard showing attendance, assignments, and certificate eligibility.", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80" },
];

const PROGRAM_COLORS = ["blue", "indigo", "pink", "emerald", "sky", "amber", "cyan", "lime", "violet", "rose", "slate", "green", "orange", "red"];
const programs = PROGRAMS.map((p, i) => ({
  id: p.id,
  icon: p.icon,
  name: p.name,
  color: PROGRAM_COLORS[i % PROGRAM_COLORS.length],
}));

const steps = [
  { n: "01", title: "Register", desc: "Fill your student & academic details on our secure portal." },
  { n: "02", title: "Pay Securely", desc: "Complete registration payment through Razorpay." },
  { n: "03", title: "Get Access", desc: "Receive login credentials instantly after verification." },
  { n: "04", title: "Attend Internship", desc: "Access courses, attendance, and assignments via dashboard." },
  { n: "05", title: "Complete Program", desc: "Finish required attendance, tasks, and assessments." },
  { n: "06", title: "Get Certificate", desc: "Download a verified certificate with a unique ID." },
];

const faqs = [
  { q: "What is the registration fee?", a: "The registration fee is shown according to the course selected during registration. It covers structured training, mentor support, attendance tracking, and a verifiable digital certificate on completion." },
  { q: "Is the certificate valid?", a: "Yes, every certificate has a unique verification ID. Anyone can verify the authenticity at /verify on our website." },
  { q: "How long is the internship?", a: "The standard internship is 120 hours. Some advanced programs may have longer durations as specified in the program details." },
  { q: "Is the internship online or offline?", a: "Most of our programs are online with flexible schedules. Some can be hybrid or offline depending on the program and your location." },
  { q: "How do I get login credentials?", a: "After successful payment and verification, your student account is created automatically. Login credentials are displayed on the success page (you can save them) and may also be emailed if email notifications are enabled." },
  { q: "Can I download my certificate?", a: "Yes, once you meet all completion criteria (attendance 75% or above, assignments completed, admin approval), the certificate becomes available for download from your dashboard." },
];

const colorMap = {
  blue: "from-blue-500 to-blue-600",
  pink: "from-pink-500 to-pink-600",
  indigo: "from-indigo-500 to-indigo-600",
  red: "from-red-500 to-red-600",
  emerald: "from-emerald-500 to-emerald-600",
  amber: "from-amber-500 to-amber-600",
  violet: "from-violet-500 to-violet-600",
  sky: "from-sky-500 to-sky-600",
  rose: "from-rose-500 to-rose-600",
  cyan: "from-cyan-500 to-cyan-600",
  lime: "from-lime-500 to-lime-600",
  slate: "from-slate-500 to-slate-600",
  green: "from-green-500 to-green-600",
  orange: "from-orange-500 to-orange-600",
};

const LandingPage = () => {
  const [activeProgramId, setActiveProgramId] = useState(null);
  const activeProgram = PROGRAMS.find((p) => p.id === activeProgramId);
  const activeInfo = activeProgramId ? PROGRAM_INFO[activeProgramId] : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 grid-bg opacity-20"></div>

        <div className="relative marketing-container py-12 sm:py-20 md:py-36">
          <div className="grid min-w-0 items-center gap-10 md:grid-cols-12 md:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="min-w-0 max-w-full md:col-span-7"
            >
              <h1 className="max-w-full break-words font-heading text-[2.55rem] font-bold leading-[1.03] tracking-tight sm:text-6xl lg:text-7xl lg:leading-[1.05]">
                Your trusted internship & <span className="text-blue-400">skill development</span> platform
              </h1>
              <p className="mt-5 max-w-full text-base leading-8 text-slate-300 sm:mt-6 sm:max-w-2xl sm:text-lg">
                Join structured 120-hour internship programs, build real-world skills, complete guided assignments, and earn a verifiable digital certificate.
              </p>

              <div className="mt-8 grid w-full max-w-full gap-3 sm:mt-10 sm:flex sm:flex-wrap">
                <Link to="/register" data-testid="hero-register-btn" className="group inline-flex min-h-12 w-full max-w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 text-center font-semibold text-white shadow-lg shadow-blue-900/40 transition-all hover:bg-blue-500 sm:w-auto sm:px-7">
                  Register Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/login" data-testid="hero-login-btn" className="inline-flex min-h-12 w-full max-w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3.5 text-center font-semibold text-white backdrop-blur transition-all hover:bg-white/20 sm:w-auto sm:px-7">
                  Student Login
                </Link>
              </div>

              <div className="mt-8 grid max-w-full grid-cols-2 gap-2 sm:mt-10 sm:flex sm:flex-wrap">
                {["120-Hour Internship", "Verified Certificate", "Mentor Support", "Online + Flexible"].map((t) => (
                  <span key={t} className="inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-full border border-blue-300/25 bg-white/10 px-2.5 py-1.5 text-center text-[11px] font-bold leading-tight text-slate-100 backdrop-blur sm:w-auto sm:px-3 sm:text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-blue-300" />
                    <span className="min-w-0">{t}</span>
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="min-w-0 max-w-full md:col-span-5"
            >
              <div className="relative">
                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/10 shadow-2xl shadow-blue-900/40">
                  <img
                    src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1000&q=80"
                    alt="Students working together during an internship program"
                    className="h-[360px] w-full object-cover sm:h-[460px]"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5">
                    <div className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-bold text-blue-700">Internship Ready</div>
                    <p className="mt-3 max-w-sm text-sm leading-6 text-slate-100">
                      Learn by doing with guided projects, attendance tracking, and certificate verification.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-y border-slate-200">
        <div className="marketing-container py-8 sm:py-12">
          <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-4 md:gap-8">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <div className="rounded-2xl bg-slate-50 px-3 py-4 ring-1 ring-slate-100 md:bg-transparent md:p-0 md:ring-0">
                  <div className="font-heading text-2xl font-bold text-slate-900 md:text-4xl">{s.value}</div>
                  <div className="mt-1 text-xs font-medium text-slate-500 md:text-sm">{s.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section-padding mobile-soft-section">
        <div className="marketing-container">
          <div className="mb-10 max-w-3xl md:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Why Kampus Campanion</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
              Built for ambitious students who want real outcomes.
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
              We combine rigorous curriculum, mentor support, and transparent tracking, so you graduate with verifiable skills, not just a certificate.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-md mobile-premium-card"
              >
                <div className="relative h-36 overflow-hidden">
                  <img src={f.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="section-padding bg-white">
        <div className="marketing-container">
          <div className="mb-10 max-w-3xl md:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Programs</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
              14+ internship domains across industries.
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
              From web development to agriculture, governance to AI, choose a program aligned with your career goals.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {programs.map((p, i) => (
              <motion.button
                key={p.id}
                type="button"
                onClick={() => setActiveProgramId(p.id)}
                data-testid={`landing-program-${p.id}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="group flex min-h-24 cursor-pointer items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-1 hover:border-blue-300 hover:shadow-md mobile-premium-card sm:block sm:min-h-0 sm:p-5"
              >
                <div className={`mb-0 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${colorMap[p.color]} shadow-sm sm:mb-3 sm:h-10 sm:w-10 sm:rounded-lg`}>
                  <p.icon className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="font-heading text-sm font-semibold text-slate-900">{p.name}</div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-slate-500 transition-colors group-hover:text-blue-600">
                    Learn more <ChevronRight className="h-3 w-3" />
                  </div>
                </div>
              </motion.button>
            ))}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: programs.length * 0.03 }}
              className="flex items-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5"
            >
              <div>
                <div className="font-heading font-semibold text-slate-900 text-sm">And many more</div>
                <div className="text-xs text-slate-500 mt-1">More domains and course options available.</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-padding bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-blue-600 rounded-full blur-[120px] opacity-20"></div>
        <div className="relative marketing-container">
          <div className="mb-10 max-w-3xl md:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">How It Works</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight md:text-5xl">
              From registration to certificate in 6 clear steps.
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-2xl border border-slate-700 bg-slate-800/60 p-5 backdrop-blur transition-all hover:border-blue-500/50 sm:p-6"
              >
                <div className="text-blue-400 font-heading font-bold text-3xl mb-3">{s.n}</div>
                <div className="font-heading font-semibold text-xl mb-2">{s.title}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Verify */}
      <section className="section-padding bg-white">
        <div className="marketing-container">
          <div className="grid items-center gap-10 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Public Verification</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
                Every certificate. Verified instantly.
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
                Employers and institutions can verify any Kampus Campanion certificate using the unique certificate ID printed on the PDF.
              </p>
              <ul className="mt-6 space-y-3">
                {["Unique certificate ID per student", "Public verification page", "Tamper-proof Firebase storage", "Issued only after meeting completion criteria"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link to="/verify" data-testid="cta-verify-btn" className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition-all hover:bg-blue-700 sm:w-auto">
                Verify a Certificate <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="md:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl mobile-premium-card sm:p-8">
                <div className="border-4 border-blue-100 p-5 text-center sm:p-8">
                  <Award className="mx-auto h-12 w-12 text-blue-600" />
                  <div className="mt-5 text-xs font-bold uppercase tracking-[0.3em] text-blue-600">Certificate</div>
                  <div className="mt-3 font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Kampus Campanion</div>
                  <div className="mx-auto mt-5 h-px w-32 bg-slate-200"></div>
                  <p className="mt-5 text-sm leading-relaxed text-slate-600">
                    Verified internship completion with unique certificate ID and public validation.
                  </p>
                  <div className="mt-6 inline-flex rounded-lg border border-slate-200 px-4 py-2 font-mono text-xs text-slate-500">
                    KC-VERIFY-2026
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS / TRUST */}
      <section className="section-padding bg-slate-50">
        <div className="marketing-container">
          <div className="mx-auto mb-10 max-w-3xl text-center md:mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Student Voices</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
              Trusted by 10,000+ students across India.
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3 md:gap-6">
            {[
              { name: "Rahul Kumar", role: "B.Tech CSE, Patna, Bihar", quote: "The 120-hour Web Development internship gave me real project experience and helped me feel confident for interviews." },
              { name: "Aman Singh", role: "BCA, Patna, Bihar", quote: "Loved the structured curriculum and mentor support. The certificate and assignments made my learning clear." },
              { name: "Priya Sharma", role: "BBA, Patna, Bihar", quote: "The Financial Literacy program was practical, simple to follow, and useful for my college projects." },
            ].map((t) => (
              <div key={t.name} className="rounded-2xl border border-slate-200 bg-white p-5 mobile-premium-card sm:p-6">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed mb-4">"{t.quote}"</p>
                <div>
                  <div className="font-heading font-semibold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="marketing-container">
          <div className="grid gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">FAQ</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
                Questions, answered.
              </h2>
              <p className="mt-4 text-slate-600">
                Can't find what you need? Reach out to our team at <span className="text-blue-600">support@kampuscompanion.com</span>
              </p>
            </div>
            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:text-blue-600 hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 text-base leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-padding bg-gradient-to-br from-blue-600 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 grid-bg"></div>
        <div className="relative marketing-container text-center">
          <h2 className="mx-auto max-w-4xl font-heading text-3xl font-bold tracking-tight md:text-6xl">
            Ready to start your internship journey?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-blue-100 sm:mt-6 sm:text-xl">
            Join 10,000+ students building real skills with Kampus Campanion. Registration takes 5 minutes.
          </p>
          <div className="mt-8 grid gap-3 sm:mt-10 sm:flex sm:flex-wrap sm:justify-center">
            <Link to="/register" data-testid="cta-final-register" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 font-bold text-blue-700 shadow-lg transition-all hover:bg-blue-50">
              Register Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/programs" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-8 py-4 font-semibold backdrop-blur transition-all hover:bg-white/20">
              Browse Programs
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* PROGRAM DETAIL MODAL */}
      <Dialog open={!!activeProgramId} onOpenChange={(open) => !open && setActiveProgramId(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0">
          {activeProgram && activeInfo && (
            <>
              {/* Header band */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <activeProgram.icon className="w-7 h-7 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="font-heading text-2xl font-bold text-white">
                      {activeProgram.name}
                    </DialogTitle>
                    <p className="text-blue-100 text-sm mt-1">{activeInfo.tagline}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                        <Clock className="w-3 h-3" /> {activeProgram.duration}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                        <GraduationCap className="w-3 h-3" /> Online | Flexible
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                        Fee according to course
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6">
                <div>
                  <p className="text-slate-700 leading-relaxed">{activeProgram.desc}</p>
                </div>

                {/* Curriculum */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpenIcon className="w-4 h-4 text-blue-600" />
                    <h3 className="font-heading font-bold text-slate-900">What You'll Learn (6 Modules)</h3>
                  </div>
                  <ul className="space-y-2">
                    {activeInfo.modules.map((m, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-slate-700">{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Skills */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-blue-600" />
                    <h3 className="font-heading font-bold text-slate-900">Skills You'll Gain</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {activeInfo.skills.map((s) => (
                      <span key={s} className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Careers */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <h3 className="font-heading font-bold text-slate-900">Career Opportunities</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {activeInfo.careers.map((c) => (
                      <div key={c} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        {c}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Certificate Highlights */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-semibold text-amber-900">Verifiable Digital Certificate</div>
                      <div className="text-amber-800 mt-1">Upon completion (75% or above attendance + assignments + admin approval), download a Kampus Campanion certificate with a unique verification ID.</div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link
                    to="/register"
                    state={{ selectedProgramId: activeProgram.id }}
                    onClick={() => setActiveProgramId(null)}
                    data-testid={`modal-enroll-${activeProgram.id}`}
                    className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all"
                  >
                    Enroll Now <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setActiveProgramId(null)}
                    className="px-6 py-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;

